import React, { useState, useMemo, useEffect } from "react"
import { Container, Card, CardBody, Row, Col, Table, Modal, Form, Label, Nav, NavItem, NavLink } from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import { useNavigate } from "react-router-dom"
import classnames from "classnames"
import secureLocalStorage from "react-secure-storage"
import { fetchNodeData, FetchNodeDeleteData, FetchNodePostData, FetchNodePutData } from "utils/common-fetchData"

// ─── Select Options ────────────────────────────────────────────────────────────
const TYPE_OPTIONS = [
  { value: "Earning", label: "Earning" },
  { value: "Deduction", label: "Deduction" },
]
const CALC_TYPE_OPTIONS = [
  { value: "Percentage", label: "Percentage (%)" },
  { value: "Fixed", label: "Fixed Amount" },
]
const CALC_BASE_OPTIONS = [
  { value: "Basic", label: "Basic" },
  { value: "Gross", label: "Gross" },
  { value: "CTC", label: "CTC" },
]
const APPLICABLE_OPTIONS = [
  { value: "Full-Time", label: "Full-Time" },
  { value: "Intern", label: "Intern" },
  { value: "Both", label: "Both" },
]

// ─── Seed Data ─────────────────────────────────────────────────────────────────
const SEED_COMPONENTS = [
  {
    id: 1,
    name: "Basic Pay",
    type: "Earning",
    calcType: "Percentage",
    value: 40,
    calcBase: "CTC",
    applicableFor: "Both",
  },
  {
    id: 2,
    name: "HRA",
    type: "Earning",
    calcType: "Percentage",
    value: 50,
    calcBase: "Basic",
    applicableFor: "Both",
  },
  {
    id: 3,
    name: "DA",
    type: "Earning",
    calcType: "Percentage",
    value: 10,
    calcBase: "Basic",
    applicableFor: "Both",
  },
  {
    id: 4,
    name: "Special Allowance",
    type: "Earning",
    calcType: "Percentage",
    value: 10,
    calcBase: "Basic",
    applicableFor: "Full-Time",
  },
  {
    id: 5,
    name: "Intern Stipend",
    type: "Earning",
    calcType: "Percentage",
    value: 80,
    calcBase: "CTC",
    applicableFor: "Intern",
  },
  {
    id: 6,
    name: "Intern Allowance",
    type: "Earning",
    calcType: "Percentage",
    value: 20,
    calcBase: "CTC",
    applicableFor: "Intern",
  },
  {
    id: 7,
    name: "PF",
    type: "Deduction",
    calcType: "Percentage",
    value: 12,
    calcBase: "Basic",
    applicableFor: "Full-Time",
  },
  {
    id: 8,
    name: "ESI",
    type: "Deduction",
    calcType: "Percentage",
    value: 0.75,
    calcBase: "Gross",
    applicableFor: "Both",
  },
  {
    id: 9,
    name: "PT",
    type: "Deduction",
    calcType: "Fixed",
    value: 200,
    calcBase: null,
    applicableFor: "Both",
  },
]

const EMPTY_FORM = {
  name: "",
  type: null,
  calcType: null,
  value: "",
  calcBase: null,
  applicableFor: null,
}

// ─── Add / Edit Modal ──────────────────────────────────────────────────────────
function ComponentModal({ isOpen, onClose, form, setForm, onSubmit, isEdit, empTypes }) {
  const f = (name, val) => setForm(p => ({ ...p, [name]: val }))
  const isFixed = form.calcType?.value === "Fixed"

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <div className="modal-header">
        <h5 className="modal-title mt-0">
          {isEdit ? "Edit" : "Add"} Salary Component
        </h5>
        <button type="button" onClick={onClose} className="btn-modal-close">
          <span className="btn-modal-span">&times;</span>
        </button>
      </div>

      <div className="modal-body">
        <Form onSubmit={onSubmit}>
          <Row>
            {/* Component Name */}
            <Col md={6} className="mb-3">
              <Label>
                Component Name <span className="text-danger">*</span>
              </Label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Basic Pay, HRA, PF"
                value={form.name}
                onChange={e => f("name", e.target.value)}
                required
              />
            </Col>

            {/* Type */}
            <Col md={6} className="mb-3">
              <Label>
                Type <span className="text-danger">*</span>
              </Label>
              <Select
                placeholder="Earning / Deduction"
                classNamePrefix="select"
                options={TYPE_OPTIONS}
                value={form.type}
                onChange={e => f("type", e)}
              />
              <input
                tabIndex={-1}
                style={{ opacity: 0, height: 0, position: "absolute" }}
                value={form.type?.value || ""}
                onChange={() => { }}
                required
              />
            </Col>

            {/* Calculation Type */}
            <Col md={6} className="mb-3">
              <Label>
                Calculation Type <span className="text-danger">*</span>
              </Label>
              <Select
                placeholder="Percentage / Fixed"
                classNamePrefix="select"
                options={CALC_TYPE_OPTIONS}
                value={form.calcType}
                onChange={e => {
                  f("calcType", e)
                  if (e.value === "Fixed") f("calcBase", null)
                }}
              />
              <input
                tabIndex={-1}
                style={{ opacity: 0, height: 0, position: "absolute" }}
                value={form.calcType?.value || ""}
                onChange={() => { }}
                required
              />
            </Col>

            {/* Value */}
            <Col md={6} className="mb-3">
              <Label>
                Value <span className="text-danger">*</span>
                <small className="text-muted ms-1">
                  {isFixed ? "(₹ amount)" : "(% percentage)"}
                </small>
              </Label>
              <div className="input-group">
                <span className="input-group-text">{isFixed ? "₹" : "%"}</span>
                <input
                  type="number"
                  className="form-control"
                  placeholder={
                    isFixed ? "Enter fixed amount" : "Enter percentage"
                  }
                  value={form.value}
                  onChange={e => f("value", e.target.value)}
                  min={0}
                  step={isFixed ? 1 : 0.01}
                  required
                />
              </div>
            </Col>

            {/* Calculation Base — hidden for Fixed */}
            {!isFixed && (
              <Col md={6} className="mb-3">
                <Label>
                  Calculation Base <span className="text-danger">*</span>
                </Label>
                <Select
                  placeholder="Basic / Gross / CTC"
                  classNamePrefix="select"
                  options={CALC_BASE_OPTIONS}
                  value={form.calcBase}
                  onChange={e => f("calcBase", e)}
                />
                <input
                  tabIndex={-1}
                  style={{ opacity: 0, height: 0, position: "absolute" }}
                  value={form.calcBase?.value || ""}
                  onChange={() => { }}
                  required
                />
              </Col>
            )}

            {/* Applicable For */}
            <Col md={isFixed ? 12 : 6} className="mb-3">
              <Label>
                Applicable For <span className="text-danger">*</span>
              </Label>
              <Select
                placeholder="Full-Time / Intern / Both"
                classNamePrefix="select"
                options={empTypes}
                value={form.applicableFor}
                onChange={e => f("applicableFor", e)}
              />
              <input
                tabIndex={-1}
                style={{ opacity: 0, height: 0, position: "absolute" }}
                value={form.applicableFor?.value || ""}
                onChange={() => { }}
                required
              />
            </Col>
          </Row>

          <div className="mt-2 d-flex gap-2">
            <button type="submit" className="border-radius btn btn-purple">
              <i className="fas fa-save me-2"></i>
              {isEdit ? "Update Component" : "Add Component"}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  )
}

// ─── Components Sub-Table ──────────────────────────────────────────────────────
function ComponentsTable({ title, color, bg, items, onEdit, onDelete }) {
  return (
    <>
      <p className="fw-semibold mb-2" style={{ color }}>
        <i
          className={`fas fa-arrow-${color === "#16a34a" ? "up" : "down"} me-1`}
        ></i>
        {title}
      </p>
      <Table className="table table-bordered table-sm mb-4">
        <thead className="table-light">
          <tr>
            <th className="text-center" style={{ width: 50 }}>
              #
            </th>
            <th className="col-3">Component</th>
            <th className="text-center">Calc. Type</th>
            <th className="text-center">Value</th>
            <th className="text-center">Base</th>
            <th className="text-center">Applicable</th>
            <th className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-3">
                No {title.toLowerCase()} configured.
              </td>
            </tr>
          ) : (
            items.map((c, i) => (
              <tr key={c.id}>
                <td className="text-center">{i + 1}</td>
                <td className="fw-medium">{c.name}</td>
                <td className="text-center">
                  <span
                    className={`badge ${c.calcType === "Percentage" ? "bg-info" : "bg-secondary"
                      }`}
                  >
                    {c.calcType}
                  </span>
                </td>
                <td className="text-center fw-semibold" style={{ color }}>
                  {c.calcType === "Percentage" ? `${c.value}%` : `₹ ${c.value}`}
                </td>
                <td className="text-center">{c.calcBase || "—"}</td>
                <td className="text-center">
                  <span
                    className="badge"
                    style={{ background: "#f3e8ff", color: "#762c99" }}
                  >
                    {c.applicableFor?.label || '--'}
                  </span>
                </td>
                <td className="text-center">
                  <div className="d-flex justify-content-center gap-1">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => onEdit(c)}
                    >
                      <i className="fas fa-pencil-alt"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => onDelete(c.id)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
const SalaryStructureMaster = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(null)
  const [components, setComponents] = useState([])

  const [modalOpen, setModalOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)


  const [empTypes, setEmpTypes] = useState([])
  useEffect(() => {
    const fetchTypes = async () => {
      const token = secureLocalStorage.getItem("auth_token")
      try {
        const response = await fetchNodeData(setLoading, 'employeeType/get', token)
        setEmpTypes(
          response.data.map(item => ({ label: item.employeetype, value: item.id }))
          || [])
      } catch (err) {
        console.log(err)
      }
    }
    fetchTypes()
  }, [])

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const response = await fetchNodeData(setLoading, 'salaryComponent/get', secureLocalStorage.getItem("auth_token"))
        setComponents(response.data.map((item) => ({
          ...item,
          name: item.componentName,
          type: item.type,
          calcType: item.calculationType,
          value: item.value,
          calcBase: item.calculationBase,
          applicableFor: empTypes.find(d => d.value === item.applicableFor)
        })) || [])
      } catch (err) {
        console.log(err)
      }
    }

    if (empTypes.length)
      fetchComponents()
  }, [empTypes])

  useEffect(() => {
    document.title = "Salary Structure Master | Zithas Crm"
  }, [])

  // ── Filter by active employee type ────────────────────────────────────────
  const filtered = useMemo(
    () => {

      if (!activeTab)
        return components

      return components.filter(
        c => c.applicableFor.value === activeTab
      )
    },
    [components, activeTab]
  )

  const earnings = filtered.filter(c => c.type === "Earning")
  const deductions = filtered.filter(c => c.type === "Deduction")

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setIsEdit(false)
    setEditId(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = row => {
    setIsEdit(true)
    setEditId(row.id)
    setForm({
      name: row.name,
      type: TYPE_OPTIONS.find(o => o.value === row.type) || null,
      calcType: CALC_TYPE_OPTIONS.find(o => o.value === row.calcType) || null,
      value: row.value,
      calcBase: CALC_BASE_OPTIONS.find(o => o.value === row.calcBase) || null,
      applicableFor: row.applicableFor || null,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setForm(EMPTY_FORM)
    setEditId(null)
  }

  const handleSubmit = e => {
    e.preventDefault()
    const token = secureLocalStorage.getItem("auth_token");

    if (!form.type || !form.calcType || !form.applicableFor) {
      Swal.fire("Validation", "Please fill all required fields.", "warning")
      return
    }
    if (form.calcType.value === "Percentage" && !form.calcBase) {
      Swal.fire("Validation", "Please select a Calculation Base.", "warning")
      return
    }

    Swal.fire({
      icon: "warning",
      title: "Are you sure ?",
      text: `You want to ${isEdit ? 'update' : 'add'} Salary component.`,
      showCancelButton: true,
    }).then(async willOkay => {
      if (willOkay.isConfirmed) {
        try {
          const entry = {
            componentName: form.name,
            type: form.type.value,
            calculationType: form.calcType.value,
            value: parseFloat(form.value),
            calculationBase: form.calcBase?.value || null,
            applicableFor: form.applicableFor.value,
          }

          let response;

          if (isEdit) {
            // Update existing
            response = await FetchNodePutData(setLoading, `salaryComponent/update`, editId, entry)
            const updated = response.data.data
            setComponents(prev =>
              prev.map(item => (item.id === editId ? {
                ...updated,
                name: updated.componentName,
                type: updated.type,
                calcType: updated.calculationType,
                value: updated.value,
                calcBase: updated.calculationBase,
                applicableFor: empTypes.find(d => d.value === updated.applicableFor) || {}
              } : item))
            )
            Swal.fire("Updated!", "Salary component updated successfully.", "success")
          } else {

            // Add new
            response = await FetchNodePostData(setLoading, `salaryComponent/post`, token, entry)

            const added = response.data.data

            setComponents(prev => [{
              ...added,
              name: added.componentName,
              type: added.type,
              calcType: added.calculationType,
              value: added.value,
              calcBase: added.calculationBase,
              applicableFor: empTypes.find(d => d.value === added.applicableFor) || {}
            }, ...prev])
            Swal.fire("Added!", "Salary component added successfully.", "success")
          }

          closeModal()
        } catch (error) {
          console.log(error)
          swal("", "Something went wrong!", "error")
        }
      }
    })





    // if (isEdit) {
    //   setComponents(p => p.map(c => (c.id === editId ? { ...c, ...entry } : c)))
    //   Swal.fire("Updated!", "Component updated successfully.", "success")
    // } else {
    //   setComponents(p => [...p, { id: nextId, ...entry }])
    //   setNextId(n => n + 1)
    //   Swal.fire("Added!", "Component added successfully.", "success")
    // }
    // closeModal()
  }

  const handleDelete = id => {
    Swal.fire({
      title: "Delete component?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#762c99",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete",
    }).then(async r => {
      if (r.isConfirmed) {

        const response = await FetchNodeDeleteData(
          setLoading,
          `salaryComponent/delete`,
          id
        );

        if (response.data.success) {
          setComponents(prev => prev.filter(item => item.id !== id))
          Swal.fire("Deleted!", "Salary Component deleted successfully.", "success")
        }

      }
    })
  }


  return (
    <React.Fragment>
      {loading && <Loader />}

      <div className="page-content mb-4">
        <Container fluid>
          <Breadcrumbs
            title="Dashboard"
            breadcrumbItem="Salary Structure"
          />

          {/* Employee type tabs */}

          <Card>
            <CardBody>
              {/* Toolbar */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center justify-content-between ">
                  <Nav pills className="gap-2">
                    {empTypes?.map(t => (
                      <NavItem key={t.value}>
                        <NavLink
                          className={classnames("border-radius px-2 py-1", {
                            active: activeTab === t.value,
                          })}
                          style={
                            activeTab === t.value
                              ? {
                                background: "#762c99",
                                color: "#fff",
                                cursor: "pointer",
                                border: "1px solid #762c99",
                                userSelect: 'none'
                              }
                              : {
                                cursor: "pointer",
                                border: "1px solid #762c99",
                                color: "#762c99",
                                userSelect: 'none'
                              }
                          }
                          onClick={() => setActiveTab(prev => ((prev && prev == t.value) ? null : t.value))}
                        >
                          {/* <i className={`fas ${t.icon} me-2`}></i> */}
                          {t.label}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                </div>
                {
                  activeTab &&
                  <h5 className="mb-0 text-muted">
                    Components for&nbsp;
                    <span style={{ color: "#762c99", fontWeight: 600 }}>
                      {empTypes.find(i => i.value == activeTab)?.label}
                    </span>
                    <span
                      className="ms-2 badge"
                      style={{ background: "#f3e8ff", color: "#762c99" }}
                    >
                      {filtered.length} total
                    </span>
                  </h5>
                }

                <div>
                  <button
                    className="border-radius btn btn-purple me-3 "
                    onClick={openAdd}
                  >
                    <i className="fas fa-plus me-2"></i>Add Component
                  </button>
                  <button
                    className="border-radius btn btn-red text-white"
                    onClick={() => navigate(-1)}
                  >
                    <i className="dripicons-cross me-2 icon-center"></i>Cancel
                  </button>
                </div>
              </div>

              {/* Earnings */}
              <ComponentsTable
                title="Earnings"
                color="#16a34a"
                bg="#f0fdf4"
                items={earnings}
                onEdit={openEdit}
                onDelete={handleDelete}
              />

              {/* Deductions */}
              <ComponentsTable
                title="Deductions"
                color="#dc2626"
                bg="#fef2f2"
                items={deductions}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      <ComponentModal
        isOpen={modalOpen}
        onClose={closeModal}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        isEdit={isEdit}
        empTypes={empTypes}
      />
    </React.Fragment>
  )
}

export default SalaryStructureMaster